import HighlightedTabs from '@shared/components/tabs/HighlightedTabs';
import * as React from 'react';
import { useCallback, useContext, useEffect } from 'react';
import { Alert, InputBase, Paper, Snackbar } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {
  ContentListFilterContext,
  ContentListFilterContextValue,
} from '@view/content-management-system/context/ContentListFilterContext';
import useIsMobile from '@hooks/use-breakpoint/useIsMobile';
import { TextKeys } from '@lib/i18n/configureI18n';
import { useTranslation } from 'react-i18next';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import { useContentListFilter } from '@view/content-management-system/hooks';
import { useContentSelect } from '@view/content-management-system/context/ContetSelectContext';
import { Content } from '@data/contents/Contents.model';
import { ContentUnpublishModal } from '@view/content-management-system/components/modals/ContentUnpublishModal';
import useDisclosure from '@hooks/useDisclouser';
import { ContentDeleteModal } from '@view/content-management-system/components/modals/ContentDeleteModal';
import { ContentCopyModal } from '@view/content-management-system/components/modals/ContentCopyModal';

const ContentListTabs: React.FC<{
  contents?: Content[];
}> = ({ contents }) => {
  const contentList = contents ?? [];
  const { search, tabs } = useContext(
    ContentListFilterContext,
  ) as ContentListFilterContextValue;

  const { selectedContents, selectContents, resetSelectedContents } =
    useContentSelect();

  const { filteredList } = useContentListFilter();

  const [tabIndex, setTabIndex] = React.useState(0);
  const [searchKeyword, setSearchKeyword] = React.useState('');

  const isMobile = useIsMobile();
  const { t } = useTranslation();
  // Toast state
  const [openToast, setOpenToast] = React.useState(false);
  const [toastMessage, setToastMessage] = React.useState('');
  const [toastSeverity, setToastSeverity] = React.useState<'success' | 'error'>(
    'success',
  );

  React.useEffect(() => {
    tabs.setValue(tabIndex);
    resetSelectedContents();
  }, [tabIndex, tabs, resetSelectedContents]);

  React.useEffect(() => {
    search.setValue(searchKeyword);
  }, [search, searchKeyword]);

  const renderSearchBar = () => {
    return (
      <Paper
        component="form"
        className="subtitle2"
        sx={{
          p: '2px 2px',
          display: 'flex',
          alignItems: 'center',
          width: 663,
          height: 34,
          borderRadius: '100px',
          backgroundColor: '#fff',
        }}
      >
        <SearchIcon
          className="text-on-primary"
          sx={{
            p: '10px',
            width: isMobile ? 20 : 40,
            height: 40,
          }}
        />
        <InputBase
          onChange={(e) => setSearchKeyword(e.target.value)}
          value={searchKeyword}
          sx={{ ml: 1, flex: 1 }}
          placeholder={isMobile ? 'Search' : 'Search projects'}
          inputProps={{ 'aria-label': isMobile ? 'Search' : 'Search projects' }}
        />
      </Paper>
    );
  };

  const handleCloseToast = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    setOpenToast(false);
  };
  // Custom toast function
  const showToast = (message: string, severity: 'success' | 'error') => {
    setToastMessage(message);
    setToastSeverity(severity);
    setOpenToast(true);
  };

  const onSelectAll = useCallback(
    (e) => {
      if (e.target.checked) {
        // Select all
        selectContents(contentList.map((content) => content.id));
      } else {
        // Deselect all
        resetSelectedContents();
      }
    },
    [contentList, resetSelectedContents, selectContents],
  );

  useEffect(() => {
    resetSelectedContents();
  }, [resetSelectedContents, tabIndex]);

  const {
    isOpen: isUnpublishDialogOpen,
    onOpen: openUnpublishDialog,
    onClose: closeUnpublishDialog,
  } = useDisclosure();

  const {
    isOpen: isDeleteDialogOpen,
    onOpen: openDeleteDialog,
    onClose: closeDeleteDialog,
  } = useDisclosure();

  const {
    isOpen: isCopyDialogOpen,
    onOpen: openCopyDialog,
    onClose: closeCopyDialog,
  } = useDisclosure();

  const handleDelete = useCallback(() => {
    openDeleteDialog();
  }, [openDeleteDialog]);

  const handleUnpublish = useCallback(() => {
    openUnpublishDialog();
  }, [openUnpublishDialog]);

  const handleCopy = useCallback(() => {
    openCopyDialog();
  }, [openCopyDialog]);

  return (
    <div className="flex flex-col justify-between w-full mt-10 mb-1">
      {/* SEARCH BAR */}
      <div className="flex flex-col items-center">{renderSearchBar()}</div>
      {/* TOOL BAR */}
      <div className="flex items-center justify-between w-full mt-10 bg-white shadow">
        {/* Left Section: Checkbox + Tabs */}
        <div className="flex items-center ml-5 gap-4">
          <input
            type="checkbox"
            onChange={onSelectAll}
            className="w-4 h-4 m-2 ml-4 mr-5"
          />
          <div className="ml-5">
            <HighlightedTabs
              tabs={tabs.options}
              selectedTab={tabIndex}
              onTabChanged={setTabIndex}
            />
          </div>
        </div>

        {!!selectedContents.length && (
          <div className="flex items-center justify-between mr-5 gap-4">
            <div className="mr-5 text-sm font-bold">
              {selectedContents.length} {t(TextKeys.ProjectSelected)}
            </div>
            <div>
              <button
                className="ml-5 mr-5"
                onClick={() => {
                  handleCopy();
                }}
              >
                <ContentCopyIcon
                  className="font-bold text-tertiary"
                  fontSize="small"
                  sx={{
                    p: '10px',
                    width: isMobile ? 20 : 40,
                    height: 40,
                  }}
                />
                <span className="font-bold text-tertiary">
                  {t(TextKeys.CopyProject)}
                </span>
              </button>
              <button
                className="ml-5 mr-5"
                onClick={() => {
                  if (tabIndex === 0) handleDelete();
                  else handleUnpublish();
                }}
              >
                <DeleteIcon
                  className="font-bold text-error"
                  fontSize="small"
                  sx={{
                    p: '10px',
                    width: isMobile ? 20 : 40,
                    height: 40,
                  }}
                />
                <span className="font-bold text-error">
                  {tabIndex === 0
                    ? t(TextKeys.DeleteProject)
                    : t(TextKeys.UnpublishProject)}
                </span>
              </button>
            </div>
          </div>
        )}
      </div>
      <div>
        <Snackbar
          open={openToast}
          autoHideDuration={6000}
          onClose={handleCloseToast}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleCloseToast}
            severity={toastSeverity}
            sx={{ width: '100%' }}
          >
            {toastMessage}
          </Alert>
        </Snackbar>
      </div>
      {isUnpublishDialogOpen && (
        <ContentUnpublishModal
          selectedContentIds={selectedContents}
          onClose={closeUnpublishDialog}
          resetSelectedContents={resetSelectedContents}
        />
      )}
      {isDeleteDialogOpen && (
        <ContentDeleteModal
          selectedContentIds={selectedContents}
          onClose={closeDeleteDialog}
          resetSelectedContents={resetSelectedContents}
        />
      )}
      {isCopyDialogOpen && (
        <ContentCopyModal
          selectedContentIds={selectedContents}
          onClose={closeCopyDialog}
          resetSelectedContents={resetSelectedContents}
        />
      )}
    </div>
  );
};

export default ContentListTabs;
